import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import WizardLayout from './WizardLayout';
import StepButtons from './StepButtons';
import useWizard from '../hook/useWizard';
import Checkbox from '../../../../components/atoms/Checkbox';
import css from './InternalDistribution.css';
import collapseCss from './components/CollapsableNetwork.css';
import { INTERNAL_S3_KEY, WIZARD_INTERNAL_DISTRIBUTION } from '../consts';
import { ExpandablePanel, TextField } from '../../../../components/widgets';
import { route } from '../../../../utils/routerUtils';
import GeneralPopup, { PopupTypes } from '../../../../components/organisms/GeneralPopup';

const translatePrefix = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.SECTION_COMPONENTS.INTERNAL_DISTRIBUTION';

const InternalDistribution = ({ onSaveDraft, submitData, pageTitle, onNext, onBack, distributionsData, translate }) => {
    const { values, setFieldValue } = useFormikContext();
    const { shouldShowDraft, isAlreadyStarted } = useWizard(values);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showPopupRedirect, setPopupRedirect] = useState(false);

    const { internalDistributions } = distributionsData;
    const internalDistribution = internalDistributions.distribution;
    const isDistributionEnabled = !!(
        internalDistribution?.active &&
        internalDistribution?.config?.folder_prefix &&
        internalDistribution.config?.bucket
    );

    const isInternalDistributionSelected = isDistributionEnabled && !!values.internalDistribution;

    const handleToggleDistribution = useCallback(
        newValue => {
            if (!newValue) {
                setIsExpanded(false);
            }
            const distributionConfig = newValue
                ? {
                      id: values?.internalDistribution?.id,
                      uanId: internalDistribution?.id,
                      bucket: internalDistribution.config.bucket,
                      folder_prefix: internalDistribution.config.folder_prefix,
                      adnetworkIntegrationId: internalDistributions.id,
                  }
                : null;
            setFieldValue('internalDistribution', distributionConfig);
        },
        [setFieldValue]
    );

    const handleExpandPanel = useCallback(() => {
        setIsExpanded(prevExpanded => !prevExpanded);
    }, []);

    const renderS3Fields = () => (
        <>
            <TextField
                key="bucket"
                label={`${translatePrefix}.S3_BUCKET_LABEL`}
                disabled
                value={internalDistribution?.config?.bucket}
                id="bucket_field"
            />
            <TextField
                key="folder_prefix"
                label={`${translatePrefix}.S3_FOLDER_PREFIX_LABEL`}
                disabled
                containerClass={css.lastInputContainer}
                value={internalDistribution?.config?.folder_prefix}
                id="folder_prefix_field"
            />
        </>
    );

    const renderDistributionHeader = () => (
        <>
            {internalDistributions?.logo_path && (
                <img
                    className={css.icon}
                    src={internalDistributions.logo_path}
                    alt={`${internalDistributions.display_name} logo`}
                />
            )}
            {internalDistributions.display_name}{' '}
            {translate(
                internalDistribution?.config?.bucket
                    ? `${translatePrefix}.S3_WITH_BUCKET`
                    : `${translatePrefix}.S3_WITHOUT_BUCKET`
            )}
        </>
    );

    const onDistributionPageClick = async () => {
        if (isAlreadyStarted) {
            route('react/audiences/distributions');
        } else {
            setPopupRedirect(true);
        }
    };

    const onRedirectConfirm = useCallback(async () => {
        await onSaveDraft(WIZARD_INTERNAL_DISTRIBUTION);
        route('react/audiences/distributions');
    }, [onSaveDraft]);

    return (
        <WizardLayout
            activeKey={WIZARD_INTERNAL_DISTRIBUTION}
            title={pageTitle}
            shouldShowDraft={shouldShowDraft}
            onSaveDraft={onSaveDraft}
            submitData={submitData}
            buttons={
                <StepButtons onBack={onBack} onNext={onNext} backTitle="BUTTONS.BACK" nextTitle="STATIC.BUTTONS.NEXT" />
            }
        >
            <div className={css.container}>
                <div>
                    <Translate id={`${translatePrefix}.TITLE`} />
                </div>
                {!isDistributionEnabled && (
                    <div>
                        <Translate id={`${translatePrefix}.SUBTITLE`} />
                        <span className={css.link} onClick={onDistributionPageClick}>
                            <Translate id={`${translatePrefix}.DISTRIBUTION`} />
                        </span>
                    </div>
                )}

                <div className={css.checkboxContainer}>
                    <Checkbox
                        checked={isInternalDistributionSelected}
                        size="small"
                        disabled={!isDistributionEnabled}
                        onChange={handleToggleDistribution}
                        labelClass={css.checkmarkLabel}
                        label={<Translate id={`${translatePrefix}.ALLOW_SENDING`} />}
                    />
                </div>

                <ExpandablePanel
                    id={INTERNAL_S3_KEY}
                    key={INTERNAL_S3_KEY}
                    expanded={isExpanded}
                    disabled={!isInternalDistributionSelected}
                    content={<div>{renderS3Fields()}</div>}
                    header={renderDistributionHeader()}
                    overrideClasses={{
                        container: collapseCss.testChannelsContainerOverride,
                        headerContainer: collapseCss.testChannelsHeaderOverride,
                        contentArea: collapseCss.testChannelsContentContainerOverride,
                        arrowArea: collapseCss.testChannelsArrowAreaOverride,
                        externalHeaderArea: collapseCss.externalHeaderAreaOverride,
                    }}
                    onExpandClick={handleExpandPanel}
                    animate
                />
                <GeneralPopup
                    open={showPopupRedirect}
                    type={PopupTypes.DELETE}
                    rejectText="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.CONTINUE_EDIT"
                    title="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.SAVE_DRAFT_TITLE"
                    acceptText="STATIC.BUTTONS.SAVE"
                    text={`${translatePrefix}.DISTRIBUTION_REDIRECT_TITLE`}
                    onAccept={() => onRedirectConfirm()}
                    onReject={() => setPopupRedirect(false)}
                />
            </div>
        </WizardLayout>
    );
};

InternalDistribution.propTypes = {
    onSaveDraft: PropTypes.func,
    onNext: PropTypes.func,
    translate: PropTypes.func,
    onBack: PropTypes.func,
    pageTitle: PropTypes.string,
    submitData: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.bool,
    }),
    distributionsData: PropTypes.shape({
        allNetworks: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                name: PropTypes.string,
                isDisabled: PropTypes.bool,
            })
        ),
        internalDistributions: PropTypes.any,
        distributionNetworks: PropTypes.any,
    }),
};

export default withLocalize(InternalDistribution);
